import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_ENDPOINTS from '../../../Api/endpoints';
import { mockTopupRequests } from '../../../mockData/paymentGateways';

const TopupRequestsPage = () => {
  const auth = useSelector((state) => state.auth);
  const token = auth?.user?.token || auth?.token || '';

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [currentTab, setCurrentTab] = useState('pending');

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [currentTab, requests]);

  const loadRequests = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual API call in Backend Phase
      // const response = await axios.get(API_ENDPOINTS.TOPUP.LIST, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      // setRequests(response.data);

      // For now, use mock data
      setRequests(mockTopupRequests);
    } catch (err) {
      setError('Failed to load topup requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    switch (currentTab) {
      case 'pending':
        filtered = requests.filter(r => r.status === 'PENDING_APPROVAL');
        break;
      case 'completed':
        filtered = requests.filter(r => r.status === 'COMPLETED');
        break;
      case 'failed':
        filtered = requests.filter(r => r.status === 'FAILED');
        break;
      default:
        filtered = requests;
    }

    setFilteredRequests(filtered);
  };

  const handleOpenDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setOpenDialog(true);
    setRemarks('');
  };

  const handleAction = async () => {
    try {
      // TODO: Replace with actual API call in Backend Phase
      // if (actionType === 'approve') {
      //   await axios.post(API_ENDPOINTS.TOPUP.APPROVE(selectedRequest.id), {
      //     remarks
      //   }, {
      //     headers: { Authorization: `Bearer ${token}` }
      //   });
      // } else {
      //   await axios.post(API_ENDPOINTS.TOPUP.REJECT(selectedRequest.id), {
      //     remarks
      //   }, {
      //     headers: { Authorization: `Bearer ${token}` }
      //   });
      // }

      // Mock implementation
      setRequests(requests.map(r =>
        r.id === selectedRequest.id
          ? {
              ...r,
              status: actionType === 'approve' ? 'COMPLETED' : 'FAILED',
              approvedBy: actionType === 'approve' ? 'Finance Admin' : null,
              approvedAt: actionType === 'approve' ? new Date().toISOString() : null,
              processedAt: new Date().toISOString(),
              walletBalanceAfter: actionType === 'approve'
                ? r.walletBalanceBefore + r.netAmount
                : r.walletBalanceBefore,
              errorMessage: actionType === 'reject' ? remarks : null,
            }
          : r
      ));

      setSuccess(
        actionType === 'approve'
          ? 'Topup request approved successfully'
          : 'Topup request rejected'
      );
      setOpenDialog(false);
      setSelectedRequest(null);
    } catch (err) {
      setError(`Failed to ${actionType} topup request`);
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PENDING_APPROVAL': return 'warning';
      case 'FAILED': return 'error';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Wallet Topup Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 2 }}>
            <Tab label="Pending Approval" value="pending" />
            <Tab label="Completed" value="completed" />
            <Tab label="Failed/Rejected" value="failed" />
            <Tab label="All Requests" value="all" />
          </Tabs>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Request ID</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Gateway/Bank</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        No {currentTab !== 'all' ? currentTab : ''} requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {request.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.requestedBy}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.requestedByEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(request.requestedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(request.amount)}
                        </Typography>
                        {request.gatewayFee > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Fee: {formatCurrency(request.gatewayFee)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.paymentMethod}
                          size="small"
                          color={request.paymentMethod === 'BANK_TRANSFER' ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        {request.gatewayName ? (
                          <Box>
                            <Typography variant="body2">{request.gatewayName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.transactionId}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2">{request.bankName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Ref: {request.referenceNumber}
                            </Typography>
                            {request.depositSlip && (
                              <Link href="#" variant="caption" display="block">
                                View Deposit Slip
                              </Link>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={request.status}
                          size="small"
                          color={getStatusColor(request.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {request.status === 'PENDING_APPROVAL' ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenDialog(request, 'approve')}
                            >
                              <ApproveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDialog(request, 'reject')}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(request, 'view')}
                          >
                            <ViewIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' && 'Approve Topup Request'}
          {actionType === 'reject' && 'Reject Topup Request'}
          {actionType === 'view' && 'Topup Request Details'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Request ID:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedRequest.id}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Requested By:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {selectedRequest.requestedBy}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(selectedRequest.amount)}
                  </Typography>
                </Grid>

                {selectedRequest.gatewayFee > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Gateway Fee:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="error.main">
                        - {formatCurrency(selectedRequest.gatewayFee)}
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Net Amount:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(selectedRequest.netAmount)}
                  </Typography>
                </Grid>

                {selectedRequest.bankName && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bank:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {selectedRequest.bankName}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Reference Number:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" fontFamily="monospace">
                        {selectedRequest.referenceNumber}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              {actionType !== 'view' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={actionType === 'approve' ? 'Approval Remarks (Optional)' : 'Rejection Reason'}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  sx={{ mt: 3 }}
                  required={actionType === 'reject'}
                />
              )}

              {selectedRequest.errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {selectedRequest.errorMessage}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {actionType === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {actionType !== 'view' && (
            <Button
              onClick={handleAction}
              variant="contained"
              color={actionType === 'approve' ? 'success' : 'error'}
              disabled={actionType === 'reject' && !remarks}
            >
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopupRequestsPage;
